import React, {useEffect, useState} from 'react';
import api from '../api/axiosInstance';
import ProductFormModal from "../components/ProductFormModal";
import {Button} from "../components/Button";

const LULU_OPTIONS = {
    trimSize: ['5 x 8 in', '5.25 x 8 in', '6 x 9 in', '7 x 10 in', '8 x 10 in', '8.5 x 11 in'],
    binding: ['Perfect Bound', 'Saddle Stitch', 'Coil Bound'],
    colorType: ['Black & White', 'Color'],
    paperType: ['60# Cream', '60# White', '80# White']
};

export default function LuluPrintJobManagementPage() {
    const [books, setBooks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [modalField, setModalField] = useState(null);
    const [productModalOpen, setProductModalOpen] = useState(false);
    const [productDraft, setProductDraft] = useState(null);
    const [priceLookup, setPriceLookup] = useState({});
    const [priceLoading, setPriceLoading] = useState({});

    const [form, setForm] = useState({
        title: '',
        interiorUrl: '',
        coverUrl: '',
        pageCount: '50',
        trimSize: '6 x 9 in',
        binding: 'Perfect Bound',
        colorType: 'Black & White',
        paperType: '60# White'
    });

    useEffect(() => {
        fetchBooks();
    }, []);

    async function fetchBooks() {
        try {
            const {data} = await api.get('/admin/products/lulu-books');
            setBooks(data);
        } catch (err) {
            setError('Failed to load Lulu books');
        }
    }

    function handleChange(e) {
        const {name, value} = e.target;
        setForm(prev => ({...prev, [name]: value}));
    }

    function openModal(field) {
        setModalField(field);
    }

    function closeModal() {
        setModalField(null);
    }

    function selectOption(option) {
        setForm(prev => ({...prev, [modalField]: option}));
        closeModal();
    }

    function buildProductFromLuluJob(job) {
        return {
            title: job.title,
            alttext: `Print book with title: ${job.title}`,
            description: `Printed book (${job.printSpecs?.trimSize}, ${job.printSpecs?.binding})`,
            details: [
                `Page count: ${job.printSpecs?.pageCount}`,
                job.printSpecs?.colorType,
                job.printSpecs?.paperType,
            ].filter(Boolean),

            sku: job._id,
            supplier: "Lulu",
            quantity: 9999,
            price: "",
            image: [],
            tags: ["book", "print-on-demand"],
            categorizations: [{category: "Books", subcategory: "Printed Books"}],
            variants: [],
            options: [],
            isFeatured: false,
            newProduct: false,
            googleDisplay: false,
            daysToHoliday: 10,
            googleProductCategory: "543543",
        };
    }

    async function fetchPriceForJob(printJobId) {
        setPriceLoading(prev => ({...prev, [printJobId]: true}));

        try {
            const {data} = await api.get('/admin/products/lulu/books/price', {
                params: {printJobId}
            });

            setPriceLookup(prev => ({
                ...prev,
                [printJobId]: data
            }));
        } catch (err) {
            setPriceLookup(prev => ({
                ...prev,
                [printJobId]: {status: 'error'}
            }));
        } finally {
            setPriceLoading(prev => ({...prev, [printJobId]: false}));
        }
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            await api.post('/admin/products/lulu/books', {
                ...form,
                pageCount: Number(form.pageCount)
            });

            setForm({
                title: '',
                interiorUrl: '',
                coverUrl: '',
                pageCount: '50',
                trimSize: '6 x 9 in',
                binding: 'Perfect Bound',
                colorType: 'Black & White',
                paperType: '60# White'
            });

            fetchBooks();
        } catch (err) {
            setError('Failed to create print job');
        } finally {
            setLoading(false);
        }
    }

    function renderField(label, name, withChooser = false) {
        return (
            <div className="space-y-1">
                <input
                    className="border p-2 w-full"
                    name={name}
                    placeholder={label}
                    value={form[name]}
                    onChange={handleChange}
                />
                {withChooser && (
                    <button
                        type="button"
                        className="text-sm underline"
                        onClick={() => openModal(name)}
                    >
                        Choose {label.toLowerCase()}
                    </button>
                )}
            </div>
        );
    }

    const handleSubscribeLulu = async () => {
        try {
            await api.post('/admin/orders/subscribe-lulu-webhooks');
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.error || "Subscription failed. Check console for details.");
        }
    };

    return (
        <div className="p-6 max-w-5xl">
            <h1 className="text-xl font-semibold mb-6">Lulu Print Job Management</h1>

            <Button onClick={handleSubscribeLulu} className="bg-yellow-500 hover:bg-yellow-600">
                Subscribe to Lulu Callbacks
            </Button>

            <form onSubmit={handleSubmit} className="space-y-4 mb-10">
                <h2 className="text-lg font-medium">Create Print Job</h2>

                {renderField('Book title', 'title')}
                {renderField('Interior PDF URL', 'interiorUrl')}
                {renderField('Cover PDF URL', 'coverUrl')}

                <div className="grid grid-cols-2 gap-3">
                    {renderField('Page count', 'pageCount')}
                </div>

                <div className="grid grid-cols-2 gap-3">
                    {renderField('Trim size', 'trimSize', true)}
                    {renderField('Binding', 'binding', true)}
                    {renderField('Color type', 'colorType', true)}
                    {renderField('Paper type', 'paperType', true)}
                </div>

                <button disabled={loading} className="px-4 py-2 bg-black text-white">
                    {loading ? 'Creating…' : 'Create Print Job'}
                </button>

                {error && <div className="text-red-600">{error}</div>}
            </form>

            <h2 className="text-lg font-medium mb-3">Existing Print Jobs</h2>

            <ul className="space-y-2">
                {books.map(job => (
                    <li key={job._id} className="border p-3 rounded space-y-2">
                        <div className="font-medium">{job.title}</div>
                        <div className="text-sm text-gray-600">Print Job ID: {job.luluPrintJobId}</div>
                        <div className="text-sm">Page Count: {job.printSpecs?.pageCount}</div>
                        {job.luluPrice && (<div className="text-sm">Lulu Fulfillment Price: {Number(job.luluPrice).toFixed(2)}</div>)}
                        <div className="text-sm">
                            Created: {new Date(job.createdAt).toLocaleString()}
                        </div>

                        <Button
                            className="mt-2 px-3 py-1 border rounded text-sm hover:bg-gray-100"
                            onClick={() => {
                                setProductDraft(buildProductFromLuluJob(job));
                                setProductModalOpen(true);
                            }}
                        >
                            Add to Storefront
                        </Button>
                        <div className="flex items-center gap-3 mt-2">
                            <Button
                                className="px-3 py-1 border rounded text-sm hover:bg-gray-100"
                                disabled={priceLoading[job.luluPrintJobId]}
                                onClick={() => fetchPriceForJob(job.luluPrintJobId)}
                            >
                                {priceLoading[job.luluPrintJobId] ? 'Checking…' : 'Get Price'}
                            </Button>

                            {priceLookup[job.luluPrintJobId]?.status === 'ready' && (
                                <span
                                    className="text-sm font-medium">{priceLookup[job.luluPrintJobId].currency}{' '}
                                    {priceLookup[job.luluPrintJobId].total.toFixed(2)}</span>
                            )}

                            {priceLookup[job.luluPrintJobId]?.status === 'pending' && (
                                <span className="text-sm text-gray-500">Pricing pending</span>
                            )}

                            {priceLookup[job.luluPrintJobId]?.status === 'error' && (
                                <span className="text-sm text-red-600">Failed to fetch price</span>
                            )}
                        </div>
                    </li>
                ))}
            </ul>

            {modalField && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded w-80">
                        <h3 className="font-medium mb-4">Select {modalField}</h3>

                        <ul className="space-y-2">
                            {LULU_OPTIONS[modalField]?.map(opt => (
                                <li key={opt}>
                                    <button
                                        type="button"
                                        className="w-full text-left p-2 border rounded hover:bg-gray-100"
                                        onClick={() => selectOption(opt)}
                                    >
                                        {opt}
                                    </button>
                                </li>
                            ))}
                        </ul>

                        <button className="mt-4 text-sm underline" onClick={closeModal}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}
            <ProductFormModal
                open={productModalOpen}
                product={productDraft}
                onClose={() => {
                    setProductModalOpen(false);
                    setProductDraft(null);
                }}
                onSubmit={async (data) => {
                    await api.post("/admin/products", data);
                    setProductModalOpen(false);
                    setProductDraft(null);
                }}
            />
        </div>
    );
}
